import { useEffect, useMemo, useRef } from 'react'

import { appToaster } from '~/libs/toaster'
import {
  type BulkEmitPolicy,
  type BulkOutcome,
  type BulkTracking,
  EMPTY_BULK_TRACKING,
  isBulkTracked,
  observeBulk,
  releaseBulk,
  trackBulk,
} from '~/models/bulkResultNotice'

/** 一括操作の結果トースト 1 通。 文言は caller (spec) が作る。 */
export type BulkToast = {
  type: 'success' | 'warning' | 'error'
  title: string
  description?: string
}

/**
 * 市場ごとに違う札の読み方・文言・出し方。 文言は札の配列から caller が数えて作る
 * (スポットは通常 / ブロック・締切前 / 締切で分けて数える。 AC-BSP-116)。
 */
export type BulkResultNoticeSpec<T, R extends string> = {
  keyOf: (item: T) => string
  classify: (item: T) => BulkOutcome<R>
  /**
   * 出し方の方針 (`~/models/bulkResultNotice`)。 既定は perObservation (スポット AC-BSP-116)。
   * 時間前 (AC-BIN-112) は oncePerBatch。
   */
  emit?: BulkEmitPolicy
  successToast: (items: readonly T[]) => BulkToast
  /** 受け付けた後の最初の観測で処理中の札があった時の 1 通 (時間前は AC-BIN-008 の処理中)。 */
  pendingToast?: (items: readonly T[]) => BulkToast
  failureToast: (items: ReadonlyArray<{ item: T; reason: R }>) => BulkToast
}

export type BulkResultNotice<T> = {
  /** 受け付けた key を追い始め、 一覧を読み直す。 */
  track: (keys: readonly string[]) => void
  /** 一覧の 1 回の観測 (`observedAtMs` = その一覧の取得時刻。 React Query の `dataUpdatedAt`)。 */
  observe: (items: readonly T[] | undefined, observedAtMs: number) => void
  /** まだ結果を知らせずに追っている key か (札ごとの知らせを止めるのに使う)。 */
  isTracked: (key: string) => boolean
  /** key の追跡をやめる (1 件の操作で札ごとの知らせに戻す)。 */
  release: (key: string) => void
}

/** 近い時点の観測をまとめる窓の既定 (ms。 perObservation だけが使う)。 */
const DEFAULT_FLUSH_MS = 1500

/**
 * 自動で閉じるまでの ms。 時間前の 1 件の板取消のトースト (`useIntradayWithdrawNotice`) と
 * 同じ長さにする (成功は DS 既定、 処理中 5 秒、 失敗 7 秒)。
 */
const DURATION_MS: Record<BulkToast['type'], number | undefined> = {
  success: undefined,
  warning: 5000,
  error: 7000,
}

/** 1 回に出す札 (種類ごとに 1 通)。 */
type Notices<T, R extends string> = {
  succeeded: readonly T[]
  pending: readonly T[]
  failed: ReadonlyArray<{ item: T; reason: R }>
}

type Buffer<T, R extends string> = {
  succeeded: T[]
  pending: T[]
  failed: Array<{ item: T; reason: R }>
}

const emptyBuffer = <T, R extends string>(): Buffer<T, R> => ({
  succeeded: [],
  pending: [],
  failed: [],
})

/**
 * 一括操作の結果トースト (時間前 AC-BIN-112・スポット AC-BSP-116)。
 * 受け付けた札を追い、 一覧の観測から成功・失敗・処理中のトーストを件数で出す。 何をいつ知らせるか
 * (出し方の方針 `spec.emit` を含む) は `~/models/bulkResultNotice` の純関数が持ち、 本 hook は
 * 追跡の状態の保持・トーストの出し方だけを持つ。
 *
 * perObservation は近い時点 (`flushMs` 以内) の観測を 1 通にまとめてから出す。 oncePerBatch は
 * 観測で返った札をその観測で出す (いつ 1 通にするかは純関数が一括ごとに決めるので、 観測を跨いで
 * まとめる窓は使わない)。
 *
 * 受付時刻は、 受け付けた時点で最後に観測した一覧の取得時刻 (`observedAtMs`) を使う。
 * 比べる相手の `dataUpdatedAt` はブラウザ時計で、 画面の現在時刻 (`~/models/clock` の `now()`)
 * はサーバー時刻に寄せた値なので同じ物差しにならない。 最後に観測した取得時刻より後に取得した
 * 一覧は、 受け付けた後に送った取得 (送信中の取得は一覧の読み直しが取り消す) の結果になる。
 *
 * トーストは 1 件ずつ別の task で出す。 DS の toaster は同じ task で作った toast の最後の
 * 1 件しか残さない (`pages/SpotBidding/hooks/useSendResultNotice.ts` と同じ理由)。
 * 画面を離れたら (unmount) 出していないトーストは捨てる (AC-BIN-112: 画面を開いている時だけ出す)。
 */
export function useBulkResultNotice<T, R extends string>(
  spec: BulkResultNoticeSpec<T, R>,
  opts: {
    invalidate: () => Promise<void>
    /** 近い時点の観測をまとめる窓 (ms。 既定 1500)。 perObservation だけに効く。 */
    flushMs?: number
  },
): BulkResultNotice<T> {
  // 返す関数を安定させる (Provider の effect の依存に入る) ため、 spec と opts は ref で読む。
  const specRef = useRef(spec)
  const optsRef = useRef(opts)
  useEffect(() => {
    specRef.current = spec
    optsRef.current = opts
  })

  const tracking = useRef<BulkTracking<T, R>>(EMPTY_BULK_TRACKING)
  const lastObservedAtMs = useRef(Number.NEGATIVE_INFINITY)
  const buffer = useRef<Buffer<T, R>>(emptyBuffer())
  const flushTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const toastTimers = useRef(new Set<ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const timers = toastTimers.current
    return () => {
      if (flushTimer.current !== undefined) clearTimeout(flushTimer.current)
      for (const timer of timers) clearTimeout(timer)
      timers.clear()
    }
  }, [])

  return useMemo<BulkResultNotice<T>>(() => {
    const show = (toast: BulkToast): void => {
      const timer = setTimeout(() => {
        toastTimers.current.delete(timer)
        appToaster.create({
          type: toast.type,
          title: toast.title,
          description: toast.description,
          closable: true,
          duration: DURATION_MS[toast.type],
        })
      }, 0)
      toastTimers.current.add(timer)
    }

    const showAll = ({ succeeded, pending, failed }: Notices<T, R>): void => {
      const current = specRef.current
      // 0 件のトーストは出さない。
      if (pending.length > 0 && current.pendingToast !== undefined) {
        show(current.pendingToast(pending))
      }
      if (succeeded.length > 0) show(current.successToast(succeeded))
      if (failed.length > 0) show(current.failureToast(failed))
    }

    const flush = (): void => {
      flushTimer.current = undefined
      const buffered = buffer.current
      buffer.current = emptyBuffer()
      showAll(buffered)
    }

    return {
      track: (keys) => {
        tracking.current = trackBulk(
          tracking.current,
          keys,
          lastObservedAtMs.current,
        )
        void optsRef.current.invalidate()
      },
      observe: (items, observedAtMs) => {
        if (items === undefined) return
        lastObservedAtMs.current = Math.max(
          lastObservedAtMs.current,
          observedAtMs,
        )
        if (tracking.current.batches.length === 0) return
        const { keyOf, classify, emit = 'perObservation' } = specRef.current
        const result = observeBulk(
          tracking.current,
          items,
          keyOf,
          classify,
          observedAtMs,
          emit,
        )
        tracking.current = result.next
        if (emit === 'oncePerBatch') {
          showAll({
            succeeded: result.succeeded,
            pending: result.pendingAtFirst,
            failed: result.failed,
          })
          return
        }
        buffer.current.succeeded.push(...result.succeeded)
        buffer.current.pending.push(...result.pendingAtFirst)
        buffer.current.failed.push(...result.failed)
        const hasNews =
          result.succeeded.length > 0 ||
          result.pendingAtFirst.length > 0 ||
          result.failed.length > 0
        if (hasNews && flushTimer.current === undefined) {
          flushTimer.current = setTimeout(
            flush,
            optsRef.current.flushMs ?? DEFAULT_FLUSH_MS,
          )
        }
      },
      isTracked: (key) => isBulkTracked(tracking.current, key),
      release: (key) => {
        tracking.current = releaseBulk(tracking.current, [key])
      },
    }
  }, [])
}
